/**
 * The visitor directory panel: a top-left dropdown (beside the Layers menu and the other toolbar
 * tools; a bottom sheet in narrow embeds) that lists the scene's hotspots by folder and its
 * "Views" (extra waypoints and the tour waypoints the creator put in it). The Layers menu switches overlays on and off; the directory is content
 * visitors go to.
 *
 * - Hotspots not in a folder come first, then the folder tree, then the Views not in a folder.
 *   Same order as the editor. Inside a folder: its subfolders, then its views, then its hotspots.
 * - Folders expand and collapse (an arrow; subfolders start collapsed), show how many hotspots and
 *   views they hold (with those below them), and have a tick box that shows or hides their
 *   hotspots in the scene, and those of the folders inside them. The box is mixed when only some
 *   of them are shown.
 * - Clicking a hotspot does what clicking it in the scene does (`activateHotspot`); when its
 *   folder is unticked the folder is ticked back on first. A hotspot its own settings hide right
 *   now (visibility range, animation) is listed greyed and can't be picked.
 * - Clicking a view goes there (`goToView`): a tour waypoint on the tour, an extra waypoint in
 *   explore mode (goToExtraWaypoint). Views are in the creator's order.
 * - A search box appears when it has folders or lists more than DIRECTORY_FILTER_THRESHOLD items.
 *   It matches hotspot, view and folder names, ignoring case and accents: a folder whose name
 *   matches shows everything in it; any other match shows inside its folders, opened.
 * - Its words are the viewer's button labels (directory…), so creators can translate them in the
 *   theme editor; the title is the creator's own.
 *
 * Built with DOM APIs only (no innerHTML of authored text), so titles can't inject markup.
 */
import { DIRECTORY_FILTER_THRESHOLD } from './hotspotFolders';
import type { DirectoryFolder, DirectoryHotspot, DirectoryView, VisitorDirectoryState } from './visitorDirectory';
import { attachSheetDrag, COMPACT_EMBED_WIDTH, getButtonLabel } from './viewerUI';
import type { ViewerButtonLabels } from '../types';

/** Folders deeper than this are drawn at this indent (a narrow panel would run out of room). */
const MAX_INDENT_DEPTH = 6;

/** The panel's words, from the viewer's button labels (theme editor "Label Overrides") or their defaults. */
export function directoryLabels(labels?: ViewerButtonLabels) {
    const get = (key: keyof ViewerButtonLabels) => getButtonLabel(labels, key);
    const fill = (key: keyof ViewerButtonLabels, values: Record<string, string | number>) =>
        Object.entries(values).reduce((text, [name, value]) => text.split(`{${name}}`).join(String(value)), get(key));
    return {
        close: get('close'),
        search: get('directorySearch'),
        searchLabel: get('directorySearchLabel'),
        views: get('directoryViews'),
        noMatch: get('directoryNoMatch'),
        noMatchWithViews: get('directoryNoMatchWithViews'),
        unavailable: get('directoryUnavailable'),
        showFolder: (name: string) => fill('directoryShowFolder', { name }),
        /** A folder's count: hotspots only, or hotspots and views together ("items"). */
        count: (hotspots: number, views: number) => {
            const n = hotspots + views;
            if (views) return fill(n === 1 ? 'directoryItemCount' : 'directoryItemsCount', { n });
            return fill(n === 1 ? 'directoryHotspotCount' : 'directoryHotspotsCount', { n });
        },
    };
}

/** List icon of the toolbar button. */
export const DIRECTORY_ICON_PATH = 'M3 5h2v2H3V5zm4 0h14v2H7V5zM3 11h2v2H3v-2zm4 0h14v2H7v-2zm-4 6h2v2H3v-2zm4 0h14v2H7v-2z';

export interface DirectoryPanelOptions {
    state: VisitorDirectoryState;
    /** Toolbar button that opens / closes the panel. */
    button: HTMLElement;
    /** Empty container the panel is rendered into (positioned by the viewer's stylesheet). */
    panel: HTMLElement;
    /** The viewer container: its width decides the phone sheet, and it holds the sheet's scrim. */
    container: HTMLElement;
    /** Do what clicking the hotspot in the scene does. */
    activateHotspot: (id: string) => void;
    /** Go to a view: a tour waypoint on the tour, an extra waypoint in explore mode. */
    goToView: (view: DirectoryView) => void;
    /** Whether a hotspot is shown by its own settings right now. Default: always. */
    isHotspotAvailable?: (id: string) => boolean;
    /** The panel opened (`asSheet`: the narrow-embed bottom sheet), so the viewer closes the other tools. */
    onOpen?: (asSheet: boolean) => void;
    onOpenChange?: (open: boolean) => void;
    /** A hotspot or view was picked from the list (`waypointIndex`: the view is a tour waypoint). */
    onSelect?: (item: { kind: 'hotspot' | 'view'; id: string; waypointIndex?: number }) => void;
    /** The viewer's button labels as they are now (read on every rebuild). Default: English. */
    labels?: () => ViewerButtonLabels | undefined;
}

export interface DirectoryPanelHandle {
    /** `focus`: move keyboard focus into the panel (a visitor opened it). */
    setOpen(open: boolean, options?: { focus?: boolean }): void;
    isOpen(): boolean;
    /** Re-read the lists, the title and the words (the state's data or the labels changed). */
    rebuild(): void;
    /** Re-check which hotspots their own settings hide (cheap; call when hotspot visibility updates). */
    refreshAvailability(): void;
    destroy(): void;
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] => {
    const node = document.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
};

/**
 * The directory's chrome: its toolbar button (a list icon labelled with the title) and the empty
 * panel mountDirectoryPanel fills. Null without a toolbar.
 */
export function createDirectoryChrome(toolbar: HTMLElement | undefined, container: HTMLElement, title: string): { button: HTMLButtonElement; panel: HTMLDivElement } | null {
    if (!toolbar) return null;
    const button = el('button', 'storysplat-directory-btn');
    button.type = 'button';
    button.setAttribute('aria-label', title);
    button.setAttribute('title', title);
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', DIRECTORY_ICON_PATH);
    svg.appendChild(path);
    button.appendChild(svg);
    const panel = el('div', 'storysplat-directory-panel');
    toolbar.appendChild(button);
    container.appendChild(panel);
    return { button, panel };
}

/** Case- and accent-insensitive text for filtering. */
// Combining accents, written as escapes: raw characters here would put non-ASCII into a regex in
// the bundle, which breaks the whole viewer on a page that decodes the script as Latin-1.
const fold = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

let panelCounter = 0;

/** A drawn list entry (hotspot or view) and its search text. */
interface EntryNode {
    li: HTMLElement;
    text: string;
}

/** A drawn folder: its parts, its search text and what it holds. */
interface FolderNode {
    id: string;
    text: string;
    section: HTMLElement;
    toggle: HTMLButtonElement;
    entries: EntryNode[];
    children: FolderNode[];
    parent: FolderNode | null;
}

export function mountDirectoryPanel(options: DirectoryPanelOptions): DirectoryPanelHandle {
    const { state, button, panel, container } = options;
    const uid = `storysplat-directory-${++panelCounter}`;
    let open = false;
    let sheet = false;
    let destroyed = false;
    let query = '';
    let words = directoryLabels(options.labels?.());
    /** Folders the visitor collapsed (subfolders start collapsed), and while searching (everything starts open). */
    const collapsed = new Set<string>();
    const searchCollapsed = new Set<string>();
    /** Folders drawn before, so a subfolder only starts collapsed the first time. */
    const known = new Set<string>();
    /** Hotspot rows by id, with their availability as last drawn. */
    const rows = new Map<string, { item: HTMLButtonElement; note: HTMLElement; available: boolean }>();
    const folderChecks = new Map<string, { section: HTMLElement; input: HTMLInputElement }>();
    const folderNodes = new Map<string, FolderNode>();
    let topNodes: FolderNode[] = [];
    let topEntries: EntryNode[] = [];
    let viewEntries: EntryNode[] = [];
    let viewsSection: HTMLElement | null = null;

    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-labelledby', `${uid}-title`);
    panel.id = panel.id || `${uid}-panel`;
    button.setAttribute('aria-controls', panel.id);
    const scrim = el('div', 'storysplat-directory-scrim');
    container.appendChild(scrim);

    /** The controls arrow keys move between: what is on screen (not filtered out, not in a collapsed folder). */
    const focusables = () =>
        Array.from(panel.querySelectorAll<HTMLElement>('.storysplat-directory-filter, .storysplat-directory-body button, .storysplat-directory-body input'))
            .filter((node) => !node.closest('[hidden], .is-filtered-out, .is-collapsed > .storysplat-directory-folder-body'));

    const available = (id: string) => {
        try {
            return options.isHotspotAvailable ? options.isHotspotAvailable(id) !== false : true;
        } catch {
            return true;
        }
    };

    const drawAvailability = (id: string) => {
        const row = rows.get(id);
        if (!row) return;
        const now = available(id);
        if (row.available === now) return;
        row.available = now;
        row.item.classList.toggle('is-unavailable', !now);
        if (now) row.item.removeAttribute('aria-disabled');
        else row.item.setAttribute('aria-disabled', 'true');
        row.note.hidden = now;
    };

    const refreshChecks = () => {
        for (const [id, { section, input }] of folderChecks) {
            const check = state.folderCheckState(id);
            input.checked = check === 'on';
            input.indeterminate = check === 'mixed';
            section.classList.toggle('is-folder-hidden', !state.isFolderVisible(id));
        }
    };

    const isCollapsed = (id: string) => (query.trim() ? searchCollapsed : collapsed).has(id);
    const applyCollapsed = (node: FolderNode) => {
        const shut = isCollapsed(node.id);
        node.section.classList.toggle('is-collapsed', shut);
        node.toggle.setAttribute('aria-expanded', shut ? 'false' : 'true');
    };
    const setCollapsed = (node: FolderNode, shut: boolean) => {
        const set = query.trim() ? searchCollapsed : collapsed;
        if (shut) set.add(node.id);
        else set.delete(node.id);
        applyCollapsed(node);
    };

    const close = (returnFocus = false) => {
        setOpen(false);
        if (returnFocus) button.focus();
    };

    const pickHotspot = (h: DirectoryHotspot) => {
        if (!available(h.id)) return;
        // Tick its own folder back on (not the folders inside it).
        if (h.folderId && !state.isFolderVisible(h.folderId)) state.setFolderVisible(h.folderId, true, { only: true });
        options.onSelect?.({ kind: 'hotspot', id: h.id });
        // On a phone the sheet covers the scene: get out of the way of the hotspot.
        if (sheet) close();
        options.activateHotspot(h.id);
    };

    const buildHotspot = (h: DirectoryHotspot): EntryNode => {
        const li = el('li', 'storysplat-directory-entry');
        const item = el('button', 'storysplat-directory-item');
        item.type = 'button';
        item.dataset.hotspotId = h.id;
        const marker = el('span', 'storysplat-directory-marker');
        marker.setAttribute('aria-hidden', 'true');
        if (h.color) marker.style.setProperty('--storysplat-marker-color', h.color);
        if (h.imageUrl) {
            const img = el('img', 'storysplat-directory-thumb');
            img.alt = '';
            img.loading = 'lazy';
            img.decoding = 'async';
            img.src = h.imageUrl;
            img.addEventListener('error', () => img.remove(), { once: true });
            marker.appendChild(img);
        }
        const name = el('span', 'storysplat-directory-name', h.title);
        const note = el('span', 'storysplat-directory-note', words.unavailable);
        note.hidden = true;
        item.append(marker, name, note);
        item.addEventListener('click', (e) => {
            e.stopPropagation();
            pickHotspot(h);
        });
        rows.set(h.id, { item, note, available: true });
        drawAvailability(h.id);
        li.appendChild(item);
        return { li, text: fold(h.title) };
    };

    const buildView = (view: DirectoryView): EntryNode => {
        const li = el('li', 'storysplat-directory-entry');
        const item = el('button', 'storysplat-directory-item storysplat-directory-item--view');
        item.type = 'button';
        item.dataset.viewId = view.id;
        if (view.kind === 'waypoint') item.dataset.waypointIndex = String(view.index);
        const icon = el('span', 'storysplat-directory-view-icon');
        icon.setAttribute('aria-hidden', 'true');
        item.append(icon, el('span', 'storysplat-directory-name', view.name));
        item.addEventListener('click', (e) => {
            e.stopPropagation();
            options.onSelect?.({ kind: 'view', id: view.id, ...(view.kind === 'waypoint' ? { waypointIndex: view.index } : {}) });
            if (sheet) close();
            options.goToView(view);
        });
        li.appendChild(item);
        return { li, text: fold(view.name) };
    };

    const buildFolder = (folder: DirectoryFolder, parent: FolderNode | null): FolderNode => {
        if (!known.has(folder.id)) {
            known.add(folder.id);
            if (folder.depth > 0) collapsed.add(folder.id);
        }
        const section = el('section', 'storysplat-directory-folder');
        section.dataset.folderId = folder.id;
        section.dataset.depth = String(folder.depth);
        const row = el('div', 'storysplat-directory-folder-row');
        const toggle = el('button', 'storysplat-directory-folder-toggle');
        toggle.type = 'button';
        const bodyId = `${uid}-f-${folderNodes.size}`;
        toggle.setAttribute('aria-controls', bodyId);
        const chevron = el('span', 'storysplat-directory-chevron');
        chevron.setAttribute('aria-hidden', 'true');
        const totals = state.countsOf(folder.id);
        const name = el('span', 'storysplat-directory-folder-name', folder.name);
        const count = el('span', 'storysplat-directory-count', String(totals.hotspots + totals.views));
        count.setAttribute('aria-label', words.count(totals.hotspots, totals.views));
        toggle.append(chevron, name, count);
        row.appendChild(toggle);
        const node: FolderNode = { id: folder.id, text: fold(folder.name), section, toggle, entries: [], children: [], parent };
        folderNodes.set(folder.id, node);
        toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            setCollapsed(node, !isCollapsed(node.id));
        });
        // A tick box only where there are hotspots to show or hide (in it or below).
        if (totals.hotspots) {
            const check = el('label', 'storysplat-directory-check');
            check.title = words.showFolder(folder.name);
            const input = el('input', 'storysplat-directory-check-input');
            input.type = 'checkbox';
            input.setAttribute('aria-label', words.showFolder(folder.name));
            input.addEventListener('change', (e) => {
                e.stopPropagation();
                state.setFolderVisible(folder.id, input.checked);
            });
            input.addEventListener('click', (e) => e.stopPropagation());
            const box = el('span', 'storysplat-directory-check-box');
            box.setAttribute('aria-hidden', 'true');
            check.append(input, box);
            row.appendChild(check);
            folderChecks.set(folder.id, { section, input });
        }
        const body = el('div', 'storysplat-directory-folder-body');
        body.id = bodyId;
        if (folder.depth >= MAX_INDENT_DEPTH) body.classList.add('storysplat-directory-folder-body--flat');
        for (const child of folder.children) {
            if (!state.countsOf(child.id).hotspots && !state.countsOf(child.id).views) continue;
            const childNode = buildFolder(child, node);
            node.children.push(childNode);
            body.appendChild(childNode.section);
        }
        if (folder.views.length || folder.hotspots.length) {
            const list = el('ul', 'storysplat-directory-items');
            for (const view of folder.views) node.entries.push(buildView(view));
            for (const h of folder.hotspots) node.entries.push(buildHotspot(h));
            for (const entry of node.entries) list.appendChild(entry.li);
            body.appendChild(list);
        }
        section.append(row, body);
        applyCollapsed(node);
        return node;
    };

    const applyFilter = () => {
        const q = fold(query.trim());
        const body = panel.querySelector<HTMLElement>('.storysplat-directory-body');
        if (!body) return;
        body.classList.toggle('is-filtering', !!q);
        let shown = 0;
        const show = (entry: EntryNode, visible: boolean) => {
            entry.li.classList.toggle('is-filtered-out', !visible);
            if (visible) shown += 1;
            return visible;
        };
        for (const entry of topEntries) show(entry, !q || entry.text.includes(q));
        // A folder whose name matches shows everything in it; otherwise only what matches, in place.
        const walk = (node: FolderNode, inherited: boolean): boolean => {
            const all = !q || inherited || node.text.includes(q);
            let any = false;
            for (const child of node.children) if (walk(child, all)) any = true;
            for (const entry of node.entries) if (show(entry, all || entry.text.includes(q))) any = true;
            node.section.classList.toggle('is-filtered-out', !any);
            applyCollapsed(node);
            return any;
        };
        for (const node of topNodes) walk(node, false);
        let anyView = false;
        for (const entry of viewEntries) if (show(entry, !q || entry.text.includes(q))) anyView = true;
        viewsSection?.classList.toggle('is-filtered-out', !anyView);
        const empty = body.querySelector<HTMLElement>('.storysplat-directory-empty');
        if (empty) empty.hidden = shown > 0;
    };

    const rebuild = () => {
        if (destroyed) return;
        words = directoryLabels(options.labels?.());
        rows.clear();
        folderChecks.clear();
        folderNodes.clear();
        topNodes = [];
        topEntries = [];
        viewEntries = [];
        viewsSection = null;
        panel.replaceChildren();
        const title = state.title;
        button.setAttribute('aria-label', title);
        button.setAttribute('title', title);

        const handle = el('div', 'storysplat-directory-handle');
        handle.setAttribute('aria-hidden', 'true');
        const header = el('div', 'storysplat-directory-header');
        const heading = el('h2', 'storysplat-directory-title', title);
        heading.id = `${uid}-title`;
        const closeBtn = el('button', 'storysplat-directory-close', '×');
        closeBtn.type = 'button';
        closeBtn.setAttribute('aria-label', words.close);
        closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            close(true);
        });
        header.append(heading, closeBtn);
        panel.append(handle, header);

        const folders = state.getTopFolders(true);
        if (folders.length || state.listedCount() > DIRECTORY_FILTER_THRESHOLD) {
            const filter = el('input', 'storysplat-directory-filter');
            filter.type = 'search';
            filter.placeholder = words.search;
            filter.setAttribute('aria-label', words.searchLabel);
            filter.setAttribute('aria-controls', `${uid}-body`);
            filter.autocomplete = 'off';
            filter.value = query;
            filter.addEventListener('input', () => {
                // Every search starts with all its folders open.
                if (filter.value.trim() !== query.trim()) searchCollapsed.clear();
                query = filter.value;
                applyFilter();
            });
            panel.appendChild(filter);
        } else {
            query = '';
        }

        const body = el('div', 'storysplat-directory-body');
        body.id = `${uid}-body`;
        const unfiled = state.getUnfiled();
        if (unfiled.length) {
            const list = el('ul', 'storysplat-directory-items');
            topEntries = unfiled.map(buildHotspot);
            for (const entry of topEntries) list.appendChild(entry.li);
            body.appendChild(list);
        }
        for (const folder of folders) {
            const node = buildFolder(folder, null);
            topNodes.push(node);
            body.appendChild(node.section);
        }
        const views = state.getViews();
        if (views.length) {
            const section = el('section', 'storysplat-directory-views');
            const headingId = `${uid}-views`;
            const viewsHeading = el('h3', 'storysplat-directory-heading', words.views);
            viewsHeading.id = headingId;
            const list = el('ul', 'storysplat-directory-items');
            list.setAttribute('aria-labelledby', headingId);
            viewEntries = views.map(buildView);
            for (const entry of viewEntries) list.appendChild(entry.li);
            section.append(viewsHeading, list);
            body.appendChild(section);
            viewsSection = section;
        }
        const empty = el('p', 'storysplat-directory-empty', state.getAllViews().length ? words.noMatchWithViews : words.noMatch);
        empty.setAttribute('role', 'status');
        empty.hidden = true;
        body.appendChild(empty);
        panel.appendChild(body);
        refreshChecks();
        applyFilter();
        if (sheet) attachSheetDrag(panel, handle, () => sheet, () => close());
    };

    const updateLayout = () => {
        const width = container.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 0);
        sheet = width > 0 && width <= COMPACT_EMBED_WIDTH;
        panel.classList.toggle('storysplat-directory-panel--sheet', sheet);
        scrim.classList.toggle('visible', open && sheet);
    };

    const setOpen = (next: boolean, opts: { focus?: boolean } = {}) => {
        if (destroyed || next === open) return;
        const hadFocus = panel.contains(document.activeElement);
        open = next;
        if (open) {
            const wasSheet = sheet;
            updateLayout();
            if (sheet && !wasSheet) rebuild();
        }
        panel.classList.toggle('open', open);
        button.classList.toggle('active', open);
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        scrim.classList.toggle('visible', open && sheet);
        if (open) {
            for (const id of rows.keys()) drawAvailability(id);
            options.onOpen?.(sheet);
            if (opts.focus) {
                const first = panel.querySelector<HTMLElement>('.storysplat-directory-filter') ?? focusables()[0] ?? panel.querySelector<HTMLElement>('.storysplat-directory-close');
                first?.focus();
            }
        } else if (hadFocus) {
            button.focus();
        }
        options.onOpenChange?.(open);
    };

    // Arrow keys move between the list's controls (right / left open and close a folder, left
    // again goes to the folder it is in); Escape closes and returns to the button.
    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            close(true);
            return;
        }
        const folderToggle = (e.target as HTMLElement | null)?.closest?.('.storysplat-directory-folder-toggle');
        if (folderToggle && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
            const id = (folderToggle.closest('.storysplat-directory-folder') as HTMLElement | null)?.dataset.folderId;
            const node = id ? folderNodes.get(id) : undefined;
            if (!node) return;
            e.preventDefault();
            if (e.key === 'ArrowRight') setCollapsed(node, false);
            else if (!isCollapsed(node.id)) setCollapsed(node, true);
            else node.parent?.toggle.focus();
            return;
        }
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
        const target = e.target as HTMLElement | null;
        if (target?.classList.contains('storysplat-directory-filter') && (e.key === 'Home' || e.key === 'End')) return;
        const list = focusables();
        if (!list.length) return;
        const index = target ? list.indexOf(target) : -1;
        let next = index;
        if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = list.length - 1;
        else next = index + (e.key === 'ArrowDown' ? 1 : -1);
        if (next < 0 || next >= list.length) return;
        e.preventDefault();
        list[next].focus();
    };

    const onButtonClick = (e: Event) => {
        e.stopPropagation();
        setOpen(!open, { focus: true });
    };
    const onScrimClick = (e: Event) => {
        e.stopPropagation();
        close(true);
    };
    const stop = (e: Event) => e.stopPropagation();
    const onResize = () => {
        if (!open) return;
        const was = sheet;
        updateLayout();
        if (was !== sheet) rebuild();
    };
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-expanded', 'false');
    button.addEventListener('click', onButtonClick);
    scrim.addEventListener('click', onScrimClick);
    panel.addEventListener('keydown', onKeyDown);
    // Clicks inside the panel must not reach the canvas (they'd refocus or move the camera).
    panel.addEventListener('pointerdown', stop);
    panel.addEventListener('click', stop);
    panel.addEventListener('wheel', stop, { passive: true });
    if (typeof window !== 'undefined') window.addEventListener('resize', onResize);
    const unsubscribe = state.onChange(refreshChecks);

    updateLayout();
    rebuild();
    if (state.startOpen) setOpen(true);

    return {
        setOpen,
        isOpen: () => open,
        rebuild,
        refreshAvailability: () => {
            if (!open) return;
            for (const id of rows.keys()) drawAvailability(id);
        },
        destroy: () => {
            if (destroyed) return;
            destroyed = true;
            unsubscribe();
            button.removeEventListener('click', onButtonClick);
            scrim.removeEventListener('click', onScrimClick);
            panel.removeEventListener('keydown', onKeyDown);
            panel.removeEventListener('pointerdown', stop);
            panel.removeEventListener('click', stop);
            panel.removeEventListener('wheel', stop);
            if (typeof window !== 'undefined') window.removeEventListener('resize', onResize);
            scrim.remove();
            panel.remove();
            button.remove();
        },
    };
}
